import type { MutationTree } from 'vuex'
import { replaceData, deleteData, replaceOrDeleteData } from '../utils'
import {
  isDeviceConnected,
  getDeviceSubscriptionType,
  getLastDeviceCommunicationTimestamp,
  getLastConnectedDeviceCommunicationTimestamp
} from '@/services/device-status'
import type {
  DevicesState,
  LatestStudy,
  SetDevicesPayload,
  SetUserDevicesPayload,
  SetDevicePayload,
  SetSubscriptionMetadataPayload,
  SetIotCoreDataPayload
} from './types'
import type {
  AveragePM,
  ChangeOverTime,
  DeviceData,
  UploadStatus,
  UserCal,
  UserDeviceApiData
} from '@/types/devices'
import type {
  DeviceCoords,
  FormattedMeasurement,
  UnpackedDevice
} from '@/services/device-service/devices'
import type { UserCalibration } from '@/services/device-calibration'
import type {
  RSSIResult,
  ConnectionTypeResult,
  EoLResult,
  FirmwareResult,
  MqttHostResult,
  SmartBridgeResult,
  LastStateResult,
  StudyIdResult,
  TelemTxTypeResult,
  IsConnectedResult,
  BatteryChargingResult,
  BatteryInstalledResult,
  BatteryLowResult,
  BatteryRemainingResult,
  ErrorStateResult
} from '@/services/device-service/state'
import { DEVICE_MUTATIONS } from './types'

interface IDeviceMutations extends MutationTree<DevicesState> {
  [DEVICE_MUTATIONS.SET_ALL_DEVICES](
    state: DevicesState,
    devices: Record<string, UnpackedDevice>
  ): void
  [DEVICE_MUTATIONS.SET_ALL_USER_DEVICES](
    state: DevicesState,
    userDevices: Record<string, UserDeviceApiData>
  ): void
  [DEVICE_MUTATIONS.SET_DEVICES](state: DevicesState, devices: SetDevicesPayload): void
  [DEVICE_MUTATIONS.SET_USER_DEVICES](state: DevicesState, devices: SetUserDevicesPayload): void
  [DEVICE_MUTATIONS.SET_DEVICE](state: DevicesState, device: SetDevicePayload): void
  [DEVICE_MUTATIONS.UPDATE_COORDS](
    state: DevicesState,
    data: { deviceId: string; coords: DeviceCoords }
  ): void
  [DEVICE_MUTATIONS.UPDATE_SENSOR_READINGS](
    state: DevicesState,
    data: { deviceId: string; measurements: Record<string, FormattedMeasurement> }
  ): void
  [DEVICE_MUTATIONS.UPDATE_DEVICE_STATUS](
    state: DevicesState,
    data: { deviceId: string; deviceStatus: string[] }
  ): void
  [DEVICE_MUTATIONS.UPDATE_SENSOR_STATUS](
    state: DevicesState,
    data: { deviceId: string; sensorStatus: string[] }
  ): void
  [DEVICE_MUTATIONS.UPDATE_RSSI](state: DevicesState, data: RSSIResult): void
  [DEVICE_MUTATIONS.UPDATE_CONNECTION_TYPE](state: DevicesState, data: ConnectionTypeResult): void
  [DEVICE_MUTATIONS.UPDATE_EOL](state: DevicesState, data: EoLResult): void
  [DEVICE_MUTATIONS.UPDATE_USER_CALS](
    state: DevicesState,
    data: { deviceId: string; userCals: UserCalibration[] }
  ): void
  [DEVICE_MUTATIONS.UPDATE_FIRMWARE](state: DevicesState, data: FirmwareResult): void
  [DEVICE_MUTATIONS.UPDATE_MQTT_HOST](state: DevicesState, data: MqttHostResult): void
  [DEVICE_MUTATIONS.UPDATE_CONNECTED_SMART_BRIDGE_SERIAL](
    state: DevicesState,
    data: SmartBridgeResult
  ): void
  [DEVICE_MUTATIONS.UPDATE_AVERAGES](
    state: DevicesState,
    data: { deviceId: string; averages: AveragePM }
  ): void
  [DEVICE_MUTATIONS.UPDATE_PERCENT_CHANGED](
    state: DevicesState,
    data: { deviceId: string; percentChanged: ChangeOverTime }
  ): void
  [DEVICE_MUTATIONS.UPDATE_PUBLIC](
    state: DevicesState,
    data: { deviceId: string; isPublic: boolean }
  ): void
  [DEVICE_MUTATIONS.UPDATE_PRIVACY_ZONE](
    state: DevicesState,
    data: { deviceId: string; privacyZone: unknown }
  ): void
  [DEVICE_MUTATIONS.UPDATE_LAST_TELEMETRY](
    state: DevicesState,
    data: { deviceId: string; timestamp: string }
  ): void
  [DEVICE_MUTATIONS.UPDATE_LAST_STATE](state: DevicesState, data: LastStateResult): void
  [DEVICE_MUTATIONS.UPDATE_LAST_TELEMETRY_STUDY_ID](state: DevicesState, data: StudyIdResult): void
  [DEVICE_MUTATIONS.UPDATE_LATEST_STUDY](
    state: DevicesState,
    data: { deviceId: string; latestStudy: LatestStudy | null }
  ): void
  [DEVICE_MUTATIONS.UPDATE_TELEM_TX_TYPE](state: DevicesState, data: TelemTxTypeResult): void
  [DEVICE_MUTATIONS.UPDATE_IS_CONNECTED](state: DevicesState, data: IsConnectedResult): void
  [DEVICE_MUTATIONS.UPDATE_IS_TELEMETRY_24HRS_ACTIVE](
    state: DevicesState,
    data: { deviceId: string; isTelemetry24HrsActive: boolean }
  ): void
  [DEVICE_MUTATIONS.UPDATE_INDOOR](
    state: DevicesState,
    data: { deviceId: string; indoor: boolean }
  ): void
  [DEVICE_MUTATIONS.UPDATE_NAME](
    state: DevicesState,
    data: { deviceId: string; name: string }
  ): void
  [DEVICE_MUTATIONS.UPDATE_LOCATION_NAME](
    state: DevicesState,
    data: { deviceId: string; locationName: string }
  ): void
  [DEVICE_MUTATIONS.UPDATE_MODEL](
    state: DevicesState,
    data: { deviceId: string; model: string }
  ): void
  [DEVICE_MUTATIONS.UPDATE_ZERO_APPLIED_AT](
    state: DevicesState,
    data: { deviceId: string; zeroAppliedAt: Record<string, string> | null }
  ): void
  [DEVICE_MUTATIONS.UPDATE_ZERO_STATE](
    state: DevicesState,
    data: { deviceId: string; zeroState: Record<string, string> | null }
  ): void
  [DEVICE_MUTATIONS.UPDATE_SUBMODEL](
    state: DevicesState,
    data: { deviceId: string; submodel: string }
  ): void
  [DEVICE_MUTATIONS.UPDATE_SERIAL](
    state: DevicesState,
    data: { deviceId: string; serial: string }
  ): void
  [DEVICE_MUTATIONS.UPDATE_SHARED](
    state: DevicesState,
    data: { deviceId: string; isShared: boolean }
  ): void
  [DEVICE_MUTATIONS.UPDATE_CALIBRATION_DATE](
    state: DevicesState,
    data: { deviceId: string; calibrationDate: string | null }
  ): void
  [DEVICE_MUTATIONS.UPDATE_FLOW_CALIBRATION_FACTOR](
    state: DevicesState,
    data: { deviceId: string; flowCalibrationFactor: number | null }
  ): void
  [DEVICE_MUTATIONS.UPDATE_USER_CAL](
    state: DevicesState,
    data: { deviceId: string; userCal: UserCal | null }
  ): void
  [DEVICE_MUTATIONS.UPDATE_ERROR_STATE](state: DevicesState, data: ErrorStateResult): void
  [DEVICE_MUTATIONS.DELETE_DEVICE_BY_ID](state: DevicesState, id: string): void
  [DEVICE_MUTATIONS.SET_SUBSCRIPTION_METADATA](
    state: DevicesState,
    data: SetSubscriptionMetadataPayload[]
  ): void
  [DEVICE_MUTATIONS.SET_IOT_CORE_DATA](state: DevicesState, data: SetIotCoreDataPayload[]): void
  [DEVICE_MUTATIONS.DELETE_REMOVED_DEVICES](
    state: DevicesState,
    devices: Record<string, unknown>
  ): void
  [DEVICE_MUTATIONS.UPDATE_BATTERY_CHARGING](state: DevicesState, data: BatteryChargingResult): void
  [DEVICE_MUTATIONS.UPDATE_BATTERY_INSTALLED](
    state: DevicesState,
    data: BatteryInstalledResult
  ): void
  [DEVICE_MUTATIONS.UPDATE_BATTERY_LOW](state: DevicesState, data: BatteryLowResult): void
  [DEVICE_MUTATIONS.UPDATE_BATTERY_REMAINING](
    state: DevicesState,
    data: BatteryRemainingResult
  ): void
  [DEVICE_MUTATIONS.UPDATE_UPLOAD_STATUS](
    state: DevicesState,
    data: { deviceId: string; uploadStatus: UploadStatus }
  ): void
  [DEVICE_MUTATIONS.UPDATE_ALARMS](
    state: DevicesState,
    data: { deviceId: string; alarms: string[] | null }
  ): void
}

const mutations: IDeviceMutations = {
  [DEVICE_MUTATIONS.SET_ALL_DEVICES](state, devices) {
    for (const deviceId in devices) {
      const device = devices[deviceId]
      state.allDevices = replaceData(state.allDevices, deviceId, device)
    }
  },
  [DEVICE_MUTATIONS.SET_ALL_USER_DEVICES](state, userDevices) {
    for (const deviceId in userDevices) {
      const device = userDevices[deviceId]
      state.allUserDevices = replaceData(state.allUserDevices, deviceId, device)
    }

    // find any devices that are currently in the user device state that are not included in the latest api response
    const missingDevices = Object.keys(state.allUserDevices).filter(
      (id) => !Object.keys(userDevices).includes(id)
    )
    for (const deviceId of missingDevices) {
      state.allUserDevices = deleteData(state.allUserDevices, deviceId)
    }
  },
  [DEVICE_MUTATIONS.SET_DEVICES](state, devices) {
    for (const deviceId in devices) {
      state.accountId = replaceData(state.accountId, deviceId, devices[deviceId]['accountId'])
      if (devices[deviceId].coords) {
        state.coords = replaceData(state.coords, deviceId, {
          lng: devices[deviceId].coords['lng'],
          lat: devices[deviceId].coords['lat']
        })
      }
      state.model = replaceData(state.model, deviceId, devices[deviceId]['model'])
      state.submodel = replaceData(state.submodel, deviceId, devices[deviceId]['submodel'])
      state.serial = replaceData(state.serial, deviceId, devices[deviceId]['serial'])
      state.indoor = replaceData(state.indoor, deviceId, devices[deviceId]['indoor'])
      state.name = replaceData(state.name, deviceId, devices[deviceId]['name'])
      state.locationName = replaceData(
        state.locationName,
        deviceId,
        devices[deviceId]['locationName']
      )
      state.public = replaceData(state.public, deviceId, devices[deviceId]['isPublic'])
      state.shared = replaceData(state.shared, deviceId, devices[deviceId]['isShared'])
      state.privacyZone = replaceData(state.privacyZone, deviceId, devices[deviceId]['privacyZone'])
      state.lastTelemetry = replaceData(
        state.lastTelemetry,
        deviceId,
        devices[deviceId]['timestamp']
      )
      state.sensorReadings = replaceData(
        state.sensorReadings,
        deviceId,
        devices[deviceId]['readings']
      )
      state.sensorStatus = replaceData(
        state.sensorStatus,
        deviceId,
        devices[deviceId]['sensorStatus']
      )
      state.deviceStatus = replaceData(
        state.deviceStatus,
        deviceId,
        devices[deviceId]['deviceStatus']
      )
    }
  },
  [DEVICE_MUTATIONS.SET_USER_DEVICES](state, devices) {
    for (const deviceId in devices) {
      state.serial = replaceData(state.serial, deviceId, devices[deviceId].serial)
      state.accountId = replaceData(state.accountId, deviceId, devices[deviceId].cloud_account_id)
      state.name = replaceData(state.name, deviceId, devices[deviceId].name)
      state.locationName = replaceData(
        state.locationName,
        deviceId,
        devices[deviceId].location_name
      )
      state.model = replaceData(state.model, deviceId, devices[deviceId].model)
      state.submodel = replaceData(
        state.submodel,
        deviceId,
        parseInt(devices[deviceId].sub_model) ? `${devices[deviceId].sub_model} gases` : ''
      )
      state.shared = replaceData(state.shared, deviceId, devices[deviceId].is_shared)
      state.public = replaceData(state.public, deviceId, devices[deviceId].is_public)
      state.indoor = replaceData(state.indoor, deviceId, devices[deviceId].is_indoor)
      state.coords = replaceData(state.coords, deviceId, {
        lng: devices[deviceId].longitude,
        lat: devices[deviceId].latitude
      })
      state.firmware = replaceData(state.firmware, deviceId, devices[deviceId].firmware_version)
      state.mqttHost = replaceData(state.mqttHost, deviceId, devices[deviceId].mqtt_host)
      state.rssi = replaceData(state.rssi, deviceId, devices[deviceId].ap_rssi)
      state.connectionType = replaceData(
        state.connectionType,
        deviceId,
        devices[deviceId].connection_type
      )
      state.lastTelemetry = replaceData(
        state.lastTelemetry,
        deviceId,
        devices[deviceId].latest_timestamp
      )
      state.sensorEoL = replaceData(state.sensorEoL, deviceId, devices[deviceId].sps_eol)
      state.deviceStatus = replaceData(
        state.deviceStatus,
        deviceId,
        devices[deviceId].system_status_messages
      )
      state.sensorStatus = replaceData(
        state.sensorStatus,
        deviceId,
        devices[deviceId].sensors_status_messages
      )
      state.measurements = replaceData(state.measurements, deviceId, devices[deviceId].measurements)
      state.subscriptionType = replaceData(
        state.subscriptionType,
        deviceId,
        getDeviceSubscriptionType(devices[deviceId] as DeviceData)
      )
      state.connectedSmartBridgeModel = replaceData(
        state.connectedSmartBridgeModel,
        deviceId,
        devices[deviceId].gateway_model
      )
      state.connectedSmartBridgeSerial = replaceData(
        state.connectedSmartBridgeSerial,
        deviceId,
        devices[deviceId].gateway_serial
      )

      // If the device is connected to smart bridge the last communication will be the last telemetry or last state timestamp.
      // Otherwise, the last communication will be the most recent of any of the 3 timestamps in the iot_core_data object.
      const lastCommunicationTime = devices[deviceId].gateway_serial
        ? getLastConnectedDeviceCommunicationTimestamp(
            devices[deviceId].latest_timestamp,
            devices[deviceId].latest_state_timestamp
          )
        : getLastDeviceCommunicationTimestamp(devices[deviceId].iot_core_data)

      state.lastCommunicationTime = replaceData(
        state.lastCommunicationTime,
        deviceId,
        lastCommunicationTime
      )

      const isConnected = devices[deviceId].gateway_serial
        ? devices[deviceId].connected
        : isDeviceConnected(lastCommunicationTime)
      state.isConnected = replaceData(state.isConnected, deviceId, isConnected)

      state.telemTxType = replaceData(state.telemTxType, deviceId, devices[deviceId].telem_tx_type)
      state.batteryCharging = replaceData(
        state.batteryCharging,
        deviceId,
        devices[deviceId].battery_charging
      )
      state.batteryInstalled = replaceData(
        state.batteryInstalled,
        deviceId,
        devices[deviceId].battery_installed
      )
      state.batteryLow = replaceData(state.batteryLow, deviceId, devices[deviceId].battery_low)
      state.batteryRemaining = replaceData(
        state.batteryRemaining,
        deviceId,
        devices[deviceId].battery_remaining
      )
      if (devices[deviceId].upload_status) {
        state.uploadStatus = replaceData(
          state.uploadStatus,
          deviceId,
          devices[deviceId].upload_status
        )
      }
      state.errorState = replaceOrDeleteData(
        state.errorState,
        deviceId,
        devices[deviceId].error_state
      )
      state.alarms = replaceOrDeleteData(state.alarms, deviceId, devices[deviceId].alarms)
    }
  },
  [DEVICE_MUTATIONS.SET_DEVICE](state, device) {
    state.accountId = replaceData(state.accountId, device.deviceId, device['accountId'])
    if (device.coords) {
      state.coords = replaceData(state.coords, device.deviceId, {
        lng: device.coords['lng'],
        lat: device.coords['lat']
      })
    }
    state.model = replaceData(state.model, device.deviceId, device['model'])
    state.submodel = replaceData(state.submodel, device.deviceId, device['submodel'])
    state.serial = replaceData(state.serial, device.deviceId, device['serial'])
    state.indoor = replaceData(state.indoor, device.deviceId, device['indoor'])
    state.name = replaceData(state.name, device.deviceId, device['name'])
    state.locationName = replaceData(state.locationName, device.deviceId, device['locationName'])
    state.public = replaceData(state.public, device.deviceId, device['isPublic'])
    state.shared = replaceData(state.shared, device.deviceId, device['isShared'])
    state.privacyZone = replaceData(state.privacyZone, device.deviceId, device['privacyZone'])
    state.lastTelemetry = replaceData(state.lastTelemetry, device.deviceId, device['timestamp'])
    state.sensorReadings = replaceData(state.sensorReadings, device.deviceId, device['readings'])
  },
  [DEVICE_MUTATIONS.UPDATE_COORDS](state, data) {
    state.coords = replaceData(state.coords, data.deviceId, {
      lng: data['coords']['lng'],
      lat: data['coords']['lat']
    })
  },
  [DEVICE_MUTATIONS.UPDATE_SENSOR_READINGS](state, data) {
    state.sensorReadings = replaceData(state.sensorReadings, data.deviceId, data['measurements'])
  },
  [DEVICE_MUTATIONS.UPDATE_DEVICE_STATUS](state, data) {
    state.deviceStatus = replaceData(state.deviceStatus, data.deviceId, data.deviceStatus)
  },
  [DEVICE_MUTATIONS.UPDATE_SENSOR_STATUS](state, data) {
    state.sensorStatus = replaceData(state.sensorStatus, data.deviceId, data.sensorStatus)
  },
  [DEVICE_MUTATIONS.UPDATE_RSSI](state, data) {
    state.rssi = replaceData(state.rssi, data.deviceId, data['rssi'])
  },
  [DEVICE_MUTATIONS.UPDATE_CONNECTION_TYPE](state, data) {
    state.connectionType = replaceData(state.connectionType, data.deviceId, data['connectionType'])
  },
  [DEVICE_MUTATIONS.UPDATE_EOL](state, data) {
    state.sensorEoL = replaceData(state.sensorEoL, data.deviceId, data['spsEoL'])
  },
  [DEVICE_MUTATIONS.UPDATE_USER_CALS](state, data) {
    state.userCals = replaceData(state.userCals, data.deviceId, data['userCals'])
  },
  [DEVICE_MUTATIONS.UPDATE_FIRMWARE](state, data) {
    state.firmware = replaceData(state.firmware, data.deviceId, data['firmware'])
  },
  [DEVICE_MUTATIONS.UPDATE_MQTT_HOST](state, data) {
    state.mqttHost = replaceData(state.mqttHost, data.deviceId, data['mqttHost'])
  },
  [DEVICE_MUTATIONS.UPDATE_CONNECTED_SMART_BRIDGE_SERIAL](state, data) {
    state.connectedSmartBridgeSerial = replaceData(
      state.connectedSmartBridgeSerial,
      data.deviceId,
      data['connectedSmartBridgeSerial']
    )
  },
  [DEVICE_MUTATIONS.UPDATE_AVERAGES](state, data) {
    state.averages = replaceData(state.averages, data.deviceId, data['averages'])
  },
  [DEVICE_MUTATIONS.UPDATE_PERCENT_CHANGED](state, data) {
    state.percentChanged = replaceData(state.percentChanged, data.deviceId, data['percentChanged'])
  },
  [DEVICE_MUTATIONS.UPDATE_PUBLIC](state, data) {
    state.public = replaceData(state.public, data.deviceId, data['isPublic'])
  },
  [DEVICE_MUTATIONS.UPDATE_PRIVACY_ZONE](state, data) {
    state.privacyZone = replaceData(state.privacyZone, data.deviceId, data['privacyZone'])
  },
  [DEVICE_MUTATIONS.UPDATE_LAST_TELEMETRY](state, data) {
    state.lastTelemetry = replaceData(state.lastTelemetry, data.deviceId, data['timestamp'])
  },
  [DEVICE_MUTATIONS.UPDATE_LAST_STATE](state, data) {
    state.lastState = replaceData(state.lastState, data.deviceId, data['timestamp'])
  },
  [DEVICE_MUTATIONS.UPDATE_LAST_TELEMETRY_STUDY_ID](state, data) {
    state.lastTelemetryStudyId = replaceOrDeleteData(
      state.lastTelemetryStudyId,
      data.deviceId,
      data['lastTelemetryStudyId']
    )
  },
  [DEVICE_MUTATIONS.UPDATE_LATEST_STUDY](state, data) {
    state.latestStudy = replaceOrDeleteData(state.latestStudy, data.deviceId, data['latestStudy'])
  },
  [DEVICE_MUTATIONS.UPDATE_TELEM_TX_TYPE](state, data) {
    state.telemTxType = replaceData(state.telemTxType, data.deviceId, data.telemTxType)
  },
  [DEVICE_MUTATIONS.UPDATE_IS_CONNECTED](state, data) {
    state.isConnected = replaceData(state.isConnected, data.deviceId, data.isConnected)
  },
  [DEVICE_MUTATIONS.UPDATE_IS_TELEMETRY_24HRS_ACTIVE](state, data) {
    state.isTelemetry24HrsActive = replaceData(
      state.isTelemetry24HrsActive,
      data.deviceId,
      data.isTelemetry24HrsActive
    )
  },
  [DEVICE_MUTATIONS.UPDATE_INDOOR](state, data) {
    state.indoor = replaceData(state.indoor, data.deviceId, data['indoor'])
  },
  [DEVICE_MUTATIONS.UPDATE_NAME](state, data) {
    state.name = replaceData(state.name, data.deviceId, data['name'])
  },
  [DEVICE_MUTATIONS.UPDATE_LOCATION_NAME](state, data) {
    state.locationName = replaceData(state.locationName, data.deviceId, data['locationName'])
  },
  [DEVICE_MUTATIONS.UPDATE_MODEL](state, data) {
    state.model = replaceData(state.model, data.deviceId, data['model'])
  },
  [DEVICE_MUTATIONS.UPDATE_ZERO_APPLIED_AT](state, data) {
    state.zeroAppliedAt = replaceData(state.zeroAppliedAt, data.deviceId, data['zeroAppliedAt'])
  },
  [DEVICE_MUTATIONS.UPDATE_ZERO_STATE](state, data) {
    state.zeroState = replaceData(state.zeroState, data.deviceId, data['zeroState'])
  },
  [DEVICE_MUTATIONS.UPDATE_SUBMODEL](state, data) {
    state.submodel = replaceData(state.submodel, data.deviceId, data['submodel'])
  },
  [DEVICE_MUTATIONS.UPDATE_SERIAL](state, data) {
    state.serial = replaceData(state.serial, data.deviceId, data['serial'])
  },
  [DEVICE_MUTATIONS.UPDATE_SHARED](state, data) {
    state.shared = replaceData(state.shared, data.deviceId, data['isShared'])
  },
  [DEVICE_MUTATIONS.UPDATE_CALIBRATION_DATE](state, data) {
    state.calibrationDate = replaceData(
      state.calibrationDate,
      data.deviceId,
      data['calibrationDate']
    )
  },
  [DEVICE_MUTATIONS.UPDATE_FLOW_CALIBRATION_FACTOR](state, data) {
    state.flowCalibrationFactor = replaceData(
      state.flowCalibrationFactor,
      data.deviceId,
      data['flowCalibrationFactor']
    )
  },
  [DEVICE_MUTATIONS.UPDATE_USER_CAL](state, data) {
    state.userCal = replaceData(state.userCal, data.deviceId, data['userCal'])
  },
  [DEVICE_MUTATIONS.UPDATE_ERROR_STATE](state, data) {
    state.errorState = replaceOrDeleteData(state.errorState, data.deviceId, data['errorState'])
  },
  [DEVICE_MUTATIONS.DELETE_DEVICE_BY_ID](state, id) {
    const mutableState = state as unknown as Record<string, Record<string, unknown>>
    for (const key in mutableState) {
      if (key === 'allUserDevices') {
        deleteData(mutableState[key], id)
        continue
      }
      mutableState[key][id] = undefined
    }
  },
  [DEVICE_MUTATIONS.SET_SUBSCRIPTION_METADATA](state, data) {
    for (const device of data) {
      const { deviceId, activation } = device
      if (activation?.website || activation?.dataservices || activation?.permissions) {
        state.subscriptionMetadata = replaceData(state.subscriptionMetadata, deviceId, activation)
      }
    }
  },
  [DEVICE_MUTATIONS.SET_IOT_CORE_DATA](state, data) {
    if (data?.length) {
      for (const device of data) {
        const { deviceId, iotCoreData } = device
        const lastCommunicationTime = getLastDeviceCommunicationTimestamp(iotCoreData)
        state.lastCommunicationTime = replaceData(
          state.lastCommunicationTime,
          deviceId,
          lastCommunicationTime
        )
      }
    }
  },
  [DEVICE_MUTATIONS.DELETE_REMOVED_DEVICES](state, devices) {
    const current = Object.keys(state.allDevices)
    const updated = Object.keys(devices)
    const removed = current.filter((id) => !updated.includes(id))
    const mutableState = state as unknown as Record<string, Record<string, unknown>>
    for (const id of removed) {
      for (const key in mutableState) {
        mutableState[key][id] = undefined
      }
    }
  },
  [DEVICE_MUTATIONS.UPDATE_BATTERY_CHARGING](state, data) {
    state.batteryCharging = replaceData(state.batteryCharging, data.deviceId, data.batteryCharging)
  },
  [DEVICE_MUTATIONS.UPDATE_BATTERY_INSTALLED](state, data) {
    state.batteryInstalled = replaceData(
      state.batteryInstalled,
      data.deviceId,
      data.batteryInstalled
    )
  },
  [DEVICE_MUTATIONS.UPDATE_BATTERY_LOW](state, data) {
    state.batteryLow = replaceData(state.batteryLow, data.deviceId, data.batteryLow)
  },
  [DEVICE_MUTATIONS.UPDATE_BATTERY_REMAINING](state, data) {
    state.batteryRemaining = replaceData(
      state.batteryRemaining,
      data.deviceId,
      data.batteryRemaining
    )
  },
  [DEVICE_MUTATIONS.UPDATE_UPLOAD_STATUS](state, data) {
    state.uploadStatus = replaceData(state.uploadStatus, data.deviceId, data.uploadStatus)
  },
  [DEVICE_MUTATIONS.UPDATE_ALARMS](state, data) {
    state.alarms = replaceData(state.alarms, data.deviceId, data.alarms)
  }
}

export default mutations
